import React from 'react';
import { Box, Popover, Tooltip, Typography } from '@mui/material';
import { TABLE_REACTION_IDS, TableReactionId } from '../../../Core/State/ITableReactionState';
import '../../../assets/css/components/PokerTableReactions.css';

export interface PokerTableReactionCatalogItem {
    id: TableReactionId;
    label: string;
    assetUrl: string;
}

export interface PokerTableReactionPoint {
    x: number;
    y: number;
}

export interface ActivePokerTableReaction {
    animationId: string;
    reactionId: TableReactionId;
    source: PokerTableReactionPoint;
    target: PokerTableReactionPoint;
}

export const POKER_TABLE_REACTION_SEND_COOLDOWN_MS = 5000;
export const POKER_TABLE_REACTION_LIFETIME_MS = 2400;

const REACTION_DISPLAY: Record<TableReactionId, Omit<PokerTableReactionCatalogItem, 'id'>> = {
    banana: { label: 'Banana', assetUrl: '/images/poker/throwables/banana.svg' },
    beer: { label: 'Beer', assetUrl: '/images/poker/throwables/beer.svg' },
    bomb: { label: 'Bomb', assetUrl: '/images/poker/throwables/bomb.svg' },
    bottle: { label: 'Bottle', assetUrl: '/images/poker/throwables/bottle.svg' },
    coin: { label: 'Coin', assetUrl: '/images/poker/throwables/coin.svg' },
    confused: { label: 'Confused', assetUrl: '/images/poker/throwables/confused.svg' },
    crown: { label: 'Crown', assetUrl: '/images/poker/throwables/crown.svg' },
    eyes: { label: 'Eyes', assetUrl: '/images/poker/throwables/eyes.svg' },
    faceInClouds: { label: 'Face in clouds', assetUrl: '/images/poker/throwables/face-in-clouds.svg' },
    fish: { label: 'Fish', assetUrl: '/images/poker/throwables/fish.svg' },
    ghost: { label: 'Ghost', assetUrl: '/images/poker/throwables/ghost.svg' },
    heartFingers: { label: 'Heart fingers', assetUrl: '/images/poker/throwables/heart-fingers.svg' },
    heartHands: { label: 'Heart hands', assetUrl: '/images/poker/throwables/heart-hands.svg' },
    heart: { label: 'Heart', assetUrl: '/images/poker/throwables/heart.svg' },
    horns: { label: 'Horns', assetUrl: '/images/poker/throwables/horns.svg' },
    lyingFace: { label: 'Lying face', assetUrl: '/images/poker/throwables/lying-face.svg' },
    moneyFace: { label: 'Money face', assetUrl: '/images/poker/throwables/money-face.svg' },
    monkeyFace: { label: 'Monkey face', assetUrl: '/images/poker/throwables/monkey-face.svg' },
    monkeyHands: { label: 'Monkey hands', assetUrl: '/images/poker/throwables/monkey-hands.svg' },
    penguin: { label: 'Penguin', assetUrl: '/images/poker/throwables/penguin.svg' },
    poop: { label: 'Poop', assetUrl: '/images/poker/throwables/poop.svg' },
    rofl: { label: 'ROFL', assetUrl: '/images/poker/throwables/rofl.svg' },
    skull: { label: 'Skull', assetUrl: '/images/poker/throwables/skull.svg' },
    starStruck: { label: 'Star struck', assetUrl: '/images/poker/throwables/star-struck.svg' },
    turtle: { label: 'Turtle', assetUrl: '/images/poker/throwables/turtle.svg' },
    worm: { label: 'Worm', assetUrl: '/images/poker/throwables/worm.svg' },
    zanyFace: { label: 'Zany face', assetUrl: '/images/poker/throwables/zany-face.svg' },
    zzz: { label: 'zzz', assetUrl: '/images/poker/throwables/zzz.svg' }
};

export const POKER_TABLE_REACTIONS: readonly PokerTableReactionCatalogItem[] = TABLE_REACTION_IDS.map((id) => ({
    id,
    ...REACTION_DISPLAY[id]
}));

const REACTION_BY_ID = new Map<TableReactionId, PokerTableReactionCatalogItem>(
    POKER_TABLE_REACTIONS.map((reaction) => [reaction.id, reaction])
);

export const getPokerTableReaction = (reactionId: TableReactionId): PokerTableReactionCatalogItem => (
    REACTION_BY_ID.get(reactionId)!
);

type PokerTableReactionScaleStyle = React.CSSProperties & {
    '--poker-table-reaction-scale': number;
};

const getReactionScaleFromAnchor = (anchorEl: HTMLElement | null): number => {
    if (!anchorEl) return 1;

    const ownerWindow = anchorEl.ownerDocument.defaultView;
    const rawScale = ownerWindow
        ?.getComputedStyle(anchorEl)
        .getPropertyValue('--poker-reaction-scale');
    const scale = Number.parseFloat(rawScale || '1');

    return Number.isFinite(scale) && scale > 0 ? scale : 1;
};

interface PokerTableReactionPickerProps {
    anchorEl: HTMLElement | null;
    targetUsername?: string;
    isSendDisabled: boolean;
    cooldownSecondsRemaining: number;
    container?: Element | null;
    onClose: () => void;
    onSelect: (reactionId: TableReactionId) => void;
}

export const PokerTableReactionPicker: React.FC<PokerTableReactionPickerProps> = ({
    anchorEl,
    targetUsername,
    isSendDisabled,
    cooldownSecondsRemaining,
    container,
    onClose,
    onSelect
}) => {
    const isOpen = Boolean(anchorEl);
    const pickerScaleStyle = React.useMemo<PokerTableReactionScaleStyle>(() => ({
        '--poker-table-reaction-scale': getReactionScaleFromAnchor(anchorEl)
    }), [anchorEl]);

    return (
        <Popover
            open={isOpen}
            anchorEl={anchorEl}
            container={container}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            PaperProps={{
                className: 'poker-table-reaction-picker-paper',
                style: pickerScaleStyle
            }}
        >
            <Box className="poker-table-reaction-picker">
                <Box className="poker-table-reaction-picker-header">
                    <Typography className="poker-table-reaction-picker-target">
                        <span className="poker-table-reaction-picker-target-prefix">Throw at</span>
                        <span className="poker-table-reaction-picker-target-name">{targetUsername || 'Player'}</span>
                    </Typography>
                    {isSendDisabled && cooldownSecondsRemaining > 0 && (
                        <Typography className="poker-table-reaction-picker-cooldown">
                            {cooldownSecondsRemaining}s
                        </Typography>
                    )}
                </Box>
                <Box className="poker-table-reaction-picker-grid">
                    {POKER_TABLE_REACTIONS.map((reaction) => (
                        <Tooltip title={`Throw ${reaction.label}`} key={reaction.id}>
                            <button
                                type="button"
                                className="poker-table-reaction-item"
                                disabled={isSendDisabled}
                                onClick={() => onSelect(reaction.id)}
                                aria-label={`Throw ${reaction.label}`}
                            >
                                <span className="poker-table-reaction-item-art">
                                    <img src={reaction.assetUrl} alt="" aria-hidden="true" />
                                </span>
                                <span className="poker-table-reaction-item-label">{reaction.label}</span>
                            </button>
                        </Tooltip>
                    ))}
                </Box>
            </Box>
        </Popover>
    );
};

interface PokerTableReactionLayerProps {
    reactions: ActivePokerTableReaction[];
}

export const PokerTableReactionLayer: React.FC<PokerTableReactionLayerProps> = ({ reactions }) => (
    <div className="poker-table-reaction-overlay" aria-hidden="true">
        {reactions.map((reaction) => {
            const item = getPokerTableReaction(reaction.reactionId);
            const dx = reaction.target.x - reaction.source.x;
            const dy = reaction.target.y - reaction.source.y;
            const lift = Math.max(64, Math.min(150, Math.hypot(dx, dy) * 0.3));
            const flyoutStyle = {
                left: `${reaction.source.x - 44}px`,
                top: `${reaction.source.y - 44}px`,
                '--reaction-dx': `${dx}px`,
                '--reaction-dy': `${dy}px`,
                '--reaction-lift': `${lift}px`
            } as React.CSSProperties;
            const targetStyle = {
                left: `${reaction.target.x}px`,
                top: `${reaction.target.y}px`
            } as React.CSSProperties;

            return (
                <React.Fragment key={reaction.animationId}>
                    <span
                        className="poker-table-reaction-flyout"
                        style={flyoutStyle}
                    >
                        <img className="poker-table-reaction-flyout-image" src={item.assetUrl} alt="" />
                    </span>
                    <span
                        className={`poker-table-reaction-stain poker-table-reaction-stain--${reaction.reactionId}`}
                        style={targetStyle}
                    />
                </React.Fragment>
            );
        })}
    </div>
);
